import axios from "axios";
import { getTenantSlug } from "./tenant";
import { handleRuntimeAccessError } from "./runtimeAccess";

// Empty in dev (relative `/api`, proxied to :8001 by CRA's package.json `proxy`); an absolute
// URL is baked in at build time for prod. Without the `|| ""` fallback an unset env var stringifies
// to the literal "undefined", producing `/undefined/api/...` 404s (breaks login in dev).
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND}/api`;

// Staff/admin auth is carried by the httpOnly `access_token` cookie set by the backend
// on login. The browser sends it automatically because of withCredentials — no token is
// ever stored in JS (localStorage), so it can't be stolen via XSS.
const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const slug = getTenantSlug();
  if (slug) config.headers["X-Tenant-Slug"] = slug;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    handleRuntimeAccessError(error);
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && Array.isArray(detail.checks)) {
    const messages = detail.checks
      .map((check) => (check && typeof check.message === "string" ? check.message.trim() : ""))
      .filter(Boolean);
    if (messages.length) return [...new Set(messages)].join(" ");
  }
  if (detail && typeof detail.error === "string") return detail.error;
  if (detail && typeof detail.code === "string") {
    return detail.code.replaceAll("_", " ").replace(/^./, (character) => character.toUpperCase());
  }
  return "The request could not be completed. Please try again.";
}

export default api;
